// client/src/components/admin/influencer/InfluencerWithdrawalModal.js
// 관리자 회원탈퇴(개인정보 하드 삭제) 전용 모달
// - 오픈 시 withdrawal-check 호출 → 차단/경고/미리보기 표시
// - 이력 차단(v1 불가)이 있으면 고정 안내만 표시
// - 해소 가능 차단은 항목별 안내 + "다시 확인" 버튼 (모달 재오픈 불필요)
// - 실행 후 결과 화면을 모달 안에 유지, 닫을 때 onWithdrawn 호출
import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

const BLOCKER_LABELS = {
  ACTIVE_CONTRACT: '진행 중인 계약/선정',
  PENDING_EXCHANGE: '처리 중인 환전 신청',
  REMAINING_POINTS: '잔여 포인트',
  CPS_UNSETTLED: '미결산 CPS 회차',
  FAILURE_LOG_OPEN: '미해결 처리 실패 로그',
  CONTRACT_HISTORY: '계약 이력',
  EXCHANGE_HISTORY: '환전 이력',
  CPS_HISTORY: 'CPS 결산 이력'
};

const CHANNEL_OPTIONS = [
  { value: 'kakao', label: '카카오 채널' },
  { value: 'inquiry', label: '문의하기' },
  { value: 'email', label: '이메일' },
  { value: 'phone', label: '전화/문자' },
  { value: 'etc', label: '기타' }
];

const WARNING_TEXTS = {
  INQUIRY_CONTENT_CHECK: (w) =>
    `문의 내역 ${w.count}건 — 본문은 보존되고 작성자 정보만 익명화됩니다. 본문에 개인정보가 있는지 문의 관리에서 먼저 점검하세요.`,
  SUSPENDED_USER: () => '활동 정지 상태의 계정입니다. 탈퇴 시 정지 기록도 함께 삭제됩니다.',
  CONTRACT_MAIL_SENT: () => '계약서 메일 발송 이력이 있습니다. 탈퇴 후 Gmail 발신함의 계약서 메일을 수동으로 파기하세요.'
};

function InfluencerWithdrawalModal({ open, onClose, userId, onWithdrawn }) {
  const [check, setCheck] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');

  const [requestChannel, setRequestChannel] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [confirmUserId, setConfirmUserId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null); // 실행 결과 (모달 안에 유지)

  const fetchCheck = useCallback(async () => {
    if (!userId) return;
    setCheckLoading(true);
    setCheckError('');
    try {
      const res = await fetch(
        `${API_URL}/api/admin/influencer/info/${userId}/withdrawal-check`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (data.ok) {
        setCheck(data);
      } else {
        setCheck(null);
        setCheckError(data.message || '확인 정보를 불러오지 못했습니다.');
      }
    } catch (err) {
      console.error('withdrawal-check error:', err);
      setCheck(null);
      setCheckError('확인 정보를 불러오는 중 오류가 발생했습니다.');
    }
    setCheckLoading(false);
  }, [userId]);

  useEffect(() => {
    if (open) {
      setCheck(null);
      setCheckError('');
      setRequestChannel('');
      setRequestNote('');
      setConfirmUserId('');
      setSubmitError('');
      setResult(null);
      fetchCheck();
    }
  }, [open, fetchCheck]);

  if (!open || !userId) return null;

  const norm = (s) => String(s || '').trim().toLowerCase();
  const canExecute =
    check && check.configured && !check.hasV1Block && check.blockers.length === 0 &&
    CHANNEL_OPTIONS.some(o => o.value === requestChannel) &&
    requestNote.trim().length >= 1 && requestNote.trim().length <= 500 &&
    norm(confirmUserId) === norm(userId) &&
    !isProcessing;

  const handleExecute = async () => {
    if (!canExecute) return;
    setSubmitError('');
    setIsProcessing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/influencer/info/${userId}/withdrawal`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            confirmUserId: confirmUserId.trim(),
            requestChannel,
            requestNote: requestNote.trim()
          })
        }
      );
      const data = await res.json();
      if (data.ok) {
        setResult(data);
      } else if (res.status === 409 && data.blockers) {
        // 실행 사이에 차단 조건이 생김 → 확인 데이터 갱신
        setSubmitError('처리 중 차단 조건이 발견되어 탈퇴가 중단되었습니다. 아래 목록을 확인하세요.');
        setCheck(prev => prev ? { ...prev, blockers: data.blockers, hasV1Block: data.blockers.some(b => b.v1Impossible) } : prev);
      } else {
        setSubmitError(data.message || '탈퇴 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('withdrawal error:', err);
      setSubmitError('탈퇴 처리 중 오류가 발생했습니다. 감사 로그를 확인하세요.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    if (result) {
      onWithdrawn && onWithdrawn();
    }
    onClose();
  };

  const renderBlockerItem = (b) => {
    if (b.code === 'REMAINING_POINTS') {
      return (
        <li key={b.code} style={{ marginBottom: 6 }}>
          <strong>{BLOCKER_LABELS[b.code]}</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
            {b.points > 0 && <div>보유 포인트: {b.points.toLocaleString()}pt — 포인트 차감으로 소멸 처리 후 탈퇴 가능 (메모: "회원탈퇴 요청에 따른 잔여 포인트 소멸")</div>}
            {b.pendingExchangePoints > 0 && <div>환전 처리 중 포인트: {b.pendingExchangePoints.toLocaleString()}pt — 환전 완료 전 탈퇴 불가</div>}
          </div>
        </li>
      );
    }
    return (
      <li key={b.code} style={{ marginBottom: 6 }}>
        <strong>{BLOCKER_LABELS[b.code] || b.code}</strong>
        {typeof b.count === 'number' && <span> — {b.count}건</span>}
      </li>
    );
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const contentStyle = {
    background: '#fff', borderRadius: 8, padding: 24, width: 560,
    maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto'
  };
  const boxStyle = (bg, border, color) => ({
    background: bg, border: `1px solid ${border}`, color,
    borderRadius: 6, padding: 12, fontSize: 13, marginBottom: 14, lineHeight: 1.6
  });

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, marginBottom: 16, color: '#b91c1c' }}>회원탈퇴 (개인정보 삭제)</h3>

        {/* 결과 화면 — 모달 안에 유지 */}
        {result ? (
          <div>
            <div style={boxStyle(
              result.partial ? '#fff7ed' : '#e8f5e9',
              result.partial ? '#fdba74' : '#a5d6a7',
              result.partial ? '#9a3412' : '#2e7d32'
            )}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {result.partial ? '⚠️ 부분 완료' : '✅ 탈퇴 완료'}
              </div>
              <div>{result.message}</div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                감사 로그 ID: {result.logId}
                {result.partial && ' — S3 잔여 키는 감사 로그(partial_s3_failed)에서 확인 후 수동 삭제하세요.'}
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
              탈퇴 요청자에게는 접수된 채널로 처리 결과를 회신하세요.
              (문구: "회원 식별 정보는 삭제되었으며, 법령상 보존 의무가 있는 이력은 관계 법령에 따라 보존됩니다.")
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleClose} style={{ padding: '8px 20px', cursor: 'pointer' }}>닫기</button>
            </div>
          </div>
        ) : checkLoading ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#666' }}>확인 중...</div>
        ) : checkError ? (
          <div>
            <div style={boxStyle('#fdecea', '#f5c6cb', '#b91c1c')}>{checkError}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={fetchCheck} style={{ padding: '8px 16px', cursor: 'pointer' }}>다시 확인</button>
              <button onClick={handleClose} style={{ padding: '8px 16px', cursor: 'pointer' }}>닫기</button>
            </div>
          </div>
        ) : check ? (
          <div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              대상: <strong>{check.user.userId}</strong> ({check.user.userName})
            </div>

            {!check.configured && (
              <div style={boxStyle('#fdecea', '#f5c6cb', '#b91c1c')}>
                서버에 탈퇴 기능 설정(WITHDRAWAL_HASH_SECRET)이 없어 실행할 수 없습니다. 서버 환경변수를 먼저 설정하세요.
              </div>
            )}

            {/* v1 불가 (이력 차단) — 고정 문구, "처리 후 가능" 안내 숨김 */}
            {check.hasV1Block ? (
              <div>
                <div style={boxStyle('#fdecea', '#f5c6cb', '#b91c1c')}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    이 회원은 계약·환전·CPS 이력이 있어 현재 버전에서 탈퇴할 수 없습니다. (추후 업데이트 지원)
                  </div>
                  법령상 거래기록 보존 의무 안내와 함께 삭제 제한을 통지하고, 대체 조치(활동 정지·마케팅 수신 해제)를 진행하세요.
                </div>
                <ul style={{ paddingLeft: 20, fontSize: 14, marginBottom: 16 }}>
                  {check.blockers.map(renderBlockerItem)}
                </ul>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={fetchCheck} style={{ padding: '8px 16px', cursor: 'pointer' }}>다시 확인</button>
                  <button onClick={handleClose} style={{ padding: '8px 16px', cursor: 'pointer' }}>닫기</button>
                </div>
              </div>
            ) : check.blockers.length > 0 ? (
              /* 해소 가능 차단 — 항목별 안내 + 다시 확인 */
              <div>
                <div style={boxStyle('#fff7ed', '#fdba74', '#9a3412')}>
                  아래 항목을 처리한 뒤 탈퇴할 수 있습니다. 처리 후 "다시 확인"을 누르세요.
                </div>
                <ul style={{ paddingLeft: 20, fontSize: 14, marginBottom: 16 }}>
                  {check.blockers.map(renderBlockerItem)}
                </ul>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={fetchCheck} style={{ padding: '8px 16px', cursor: 'pointer' }}>다시 확인</button>
                  <button onClick={handleClose} style={{ padding: '8px 16px', cursor: 'pointer' }}>닫기</button>
                </div>
              </div>
            ) : (
              /* 실행 가능 — 미리보기 + 경고 + 입력 폼 */
              <div>
                <div style={boxStyle('#fdecea', '#f5c6cb', '#b91c1c')}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ 되돌릴 수 없는 작업입니다</div>
                  회원 정보(이름·연락처·주소·계좌·SNS 정보·인증 이미지)가 즉시 영구 삭제됩니다.
                  아이디는 재사용 가능해지며, 문의 내역은 본문만 보존되고 작성자 정보는 익명화됩니다.
                </div>

                {check.warnings.length > 0 && (
                  <div style={boxStyle('#fff7ed', '#fdba74', '#9a3412')}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>실행 전 확인 사항</div>
                    {check.warnings.map(w => (
                      <div key={w.code} style={{ marginBottom: 4 }}>
                        • {WARNING_TEXTS[w.code] ? WARNING_TEXTS[w.code](w) : w.code}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 13, color: '#555', marginBottom: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>삭제 대상</div>
                  SNS 정보 {check.preview.snsInfo}건 · 인증 이미지 {check.preview.snsCertiImg}건 ·
                  SNS 추가 요청 {check.preview.snsAddRequest}건 · 프로젝트 지원서 {check.preview.projectApplication}건 ·
                  문의 익명화 {check.preview.inquiryToAnonymize}건
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    탈퇴 요청 경로 <span style={{ color: '#b91c1c' }}>*</span>
                  </label>
                  <select
                    value={requestChannel}
                    onChange={(e) => setRequestChannel(e.target.value)}
                    style={{ width: '100%', padding: 8, fontSize: 14 }}
                  >
                    <option value="">선택하세요</option>
                    {CHANNEL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {(requestChannel === 'phone' || requestChannel === 'etc') && (
                    <div style={{ fontSize: 12, color: '#9a3412', marginTop: 4 }}>
                      전화/기타 접수 건은 회신할 스레드가 없습니다. 처리 전에 카카오 채널·문의·이메일 중 회신 가능한 채널을 먼저 확보하세요.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    요청 근거 메모 <span style={{ color: '#b91c1c' }}>*</span>
                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                      ({requestNote.trim().length}/500 — 개인정보 원문 기재 금지)
                    </span>
                  </label>
                  <textarea
                    value={requestNote}
                    onChange={(e) => setRequestNote(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="예: 9/2 카카오 채널로 본인 탈퇴 요청 접수"
                    style={{ width: '100%', padding: 8, fontSize: 14, boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    확인을 위해 대상 아이디 입력 <span style={{ color: '#b91c1c' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={confirmUserId}
                    onChange={(e) => setConfirmUserId(e.target.value)}
                    placeholder={check.user.userId}
                    style={{ width: '100%', padding: 8, fontSize: 14, boxSizing: 'border-box' }}
                  />
                </div>

                {submitError && (
                  <div style={boxStyle('#fdecea', '#f5c6cb', '#b91c1c')}>{submitError}</div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={handleClose} disabled={isProcessing} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                    취소
                  </button>
                  <button
                    onClick={handleExecute}
                    disabled={!canExecute}
                    style={{
                      padding: '8px 20px',
                      background: canExecute ? '#b91c1c' : '#e5a3a3',
                      color: '#fff', border: 'none', borderRadius: 4,
                      cursor: canExecute ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {isProcessing ? '처리 중...' : '회원탈퇴 실행'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default InfluencerWithdrawalModal;
